import { useEffect } from 'react'

/**
 * Returns the selected text if the browser selection lives inside a
 * `[data-selectable-table]` element, otherwise null.
 */
function getTableSelectionText(): string | null {
  const sel = window.getSelection()
  if (!sel || sel.isCollapsed || sel.toString().length === 0) return null
  const anchor = sel.anchorNode
  if (!anchor) return null
  const el = anchor instanceof HTMLElement ? anchor : anchor.parentElement
  if (!el?.closest('[data-selectable-table]')) return null
  return sel.toString()
}

/**
 * Enables Ctrl+C / Cmd+C text copy from `[data-selectable-table]` elements
 * on the tldraw board.
 *
 * Problem: tldraw intercepts keyboard events and/or the native `copy` event,
 * so pressing Cmd+C copies the selected *shape* (filename) instead of the
 * highlighted cell text. Right-click → Copy works because it bypasses the
 * keyboard pipeline entirely.
 *
 * Solution: intercept `keydown` in the capture phase on `document`. When
 * Cmd/Ctrl+C is pressed and the selection is inside a data table, we
 * preventDefault (so tldraw never sees it) and write the text to the
 * clipboard ourselves. We also keep a capture-phase `copy` listener as a
 * fallback for right-click → Copy in case tldraw's bubble-phase listener
 * would otherwise interfere.
 *
 * Call this hook once near the board root.
 */
export function useTableTextCopy() {
  useEffect(() => {
    // Capture-phase keydown: runs before tldraw's container-level or
    // hotkeys-js listeners can swallow the event or preventDefault.
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== 'c') return

      const text = getTableSelectionText()
      if (!text) return

      // Stop the event from reaching tldraw's handlers entirely.
      e.preventDefault()
      e.stopImmediatePropagation()

      // Write to clipboard via the async Clipboard API since we can't
      // use ClipboardEvent.clipboardData outside a real copy event.
      navigator.clipboard.writeText(text).catch(() => {
        // Fallback: dispatch a synthetic copy event so the browser's
        // native copy path runs with our capture-phase copy handler below.
        document.execCommand('copy')
      })
    }

    // Capture-phase copy: handles right-click → Copy and the execCommand
    // fallback above. Runs before tldraw's bubble-phase document listener.
    const onCopy = (e: ClipboardEvent) => {
      const text = getTableSelectionText()
      if (!text) return

      e.preventDefault()
      e.stopImmediatePropagation()
      e.clipboardData?.setData('text/plain', text)
    }

    document.addEventListener('keydown', onKeyDown, true)
    document.addEventListener('copy', onCopy, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      document.removeEventListener('copy', onCopy, true)
    }
  }, [])
}
